import type { MetricDefinition, PeriodDefinition } from "@/grid/core/types";
import {
  DEFAULT_MONTHLY_TARGET_POLICY,
  DEFAULT_WEEKLY_TARGET_POLICY,
} from "@/grid/core/target-policy";

export const PERIOD_CATALOG: Record<string, PeriodDefinition> = {
  wtd: { id: "wtd", label: "Week to date", shortLabel: "WTD" },
  lastWeek: { id: "lastWeek", label: "Last week", shortLabel: "Last" },
  mtd: { id: "mtd", label: "Month to date", shortLabel: "MTD" },
  qtd: { id: "qtd", label: "Quarter to date", shortLabel: "QTD" },
};

export const METRIC_CATALOG = {
  calls: {
    id: "calls",
    label: "Calls",
    shortLabel: "Calls",
    icon: "Phone",
    targetMetric: "connected_calls",
    source: { kind: "daily-sum", field: "connected_calls" },
    format: "number",
    targetPolicy: DEFAULT_WEEKLY_TARGET_POLICY,
    weight: 1,
  },
  connectedCalls: {
    id: "connectedCalls",
    label: "Connected calls",
    shortLabel: "Connected",
    icon: "Phone",
    targetMetric: "connected_calls",
    source: { kind: "daily-sum", field: "connected_calls" },
    format: "number",
    targetPolicy: DEFAULT_WEEKLY_TARGET_POLICY,
  },
  callsTotal: {
    id: "callsTotal",
    label: "Total calls",
    shortLabel: "Total",
    icon: "Phone",
    targetMetric: "total_calls",
    source: { kind: "daily-sum", field: "total_calls" },
    format: "number",
    targetPolicy: DEFAULT_WEEKLY_TARGET_POLICY,
  },
  meetings: {
    id: "meetings",
    label: "Meetings",
    shortLabel: "Meetings",
    icon: "Calendar",
    targetMetric: "meetings_held",
    source: { kind: "daily-sum", field: "meetings_held" },
    format: "number",
    targetPolicy: DEFAULT_WEEKLY_TARGET_POLICY,
    weight: 1,
  },
  sequences: {
    id: "sequences",
    label: "Sequences",
    shortLabel: "Seq",
    icon: "Send",
    targetMetric: "sequences",
    source: { kind: "daily-sum", field: "sequences" },
    format: "number",
    targetPolicy: DEFAULT_WEEKLY_TARGET_POLICY,
    weight: 1,
  },
  sqls: {
    id: "sqls",
    label: "SQLs",
    shortLabel: "SQLs",
    icon: "Target",
    targetMetric: "sqls",
    source: { kind: "rep-period", field: "sqls" },
    format: "number",
    targetPolicy: DEFAULT_WEEKLY_TARGET_POLICY,
    weight: 3,
  },
  opps: {
    id: "opps",
    label: "Opportunities",
    shortLabel: "Opps",
    icon: "Target",
    targetMetric: "opps_generated_value",
    source: { kind: "team-period", field: "opps_generated_value" },
    format: "currency",
    targetPolicy: DEFAULT_WEEKLY_TARGET_POLICY,
    weight: 1.5,
  },
  sales: {
    id: "sales",
    label: "Sales",
    shortLabel: "Sales",
    icon: "DollarSign",
    targetMetric: "sales_amount",
    source: { kind: "team-period", field: "sales_amount" },
    format: "currency",
    targetPolicy: DEFAULT_MONTHLY_TARGET_POLICY,
    weight: 3,
  },
} as const satisfies Record<string, MetricDefinition>;

export function pickMetrics(...ids: (keyof typeof METRIC_CATALOG)[]): MetricDefinition[] {
  return ids.map((id) => METRIC_CATALOG[id]);
}

export function exploreMetricsFor(department: {
  metrics: MetricDefinition[];
  exploreMetrics?: MetricDefinition[];
}): MetricDefinition[] {
  return department.exploreMetrics ?? department.metrics;
}

/** Map legacy Explore URLs (`metric=calls`) to the current metric id. */
export function normalizeExploreMetricId(
  department: { exploreMetrics?: MetricDefinition[] },
  metricId: string,
): string {
  if (metricId === "calls" && department.exploreMetrics) {
    return "connectedCalls";
  }
  return metricId;
}

export function pickPeriods(...ids: (keyof typeof PERIOD_CATALOG)[]): PeriodDefinition[] {
  return ids.map((id) => PERIOD_CATALOG[id]);
}
